import { useContext } from "react";
import Image from "@Components/Image";
import CustomLink from "@Components/CustomLink";
import Reveal from "@Components/Reveal/Reveal";
import { ScholarshipFormContext } from "@Pages/Scholarship/ScholarshipFormModal";
import styles from "./ScholarshipCta.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import { pageAsset } from "@Utils/getStaticAssetPath";

// Images come from the static-assets bucket, never `import`ed — see `pageAsset`.
const asset = pageAsset("ScholarshipV2");

const ctaImg = asset("cta.jpg");

/**
 * Closing CTA card — Figma node 2134:9078.
 *
 * Three stacked layers over the photo, all from the node: a flat tint, a
 * left-to-right scrim that holds the copy side dark, and a bottom-up scrim over
 * the lower 190px. The copy block is bottom-left aligned inside 56px padding.
 */
/**
 * `openForm` comes from ScholarshipFormContext rather than a prop: that is the
 * contract the page this replaces used for its Apply buttons, and it keeps the
 * handler out of every component between here and the page.
 */
const Cta = () => {
  const { t } = useTranslationHook();
  const { openForm } = useContext(ScholarshipFormContext);

  return (
    <section className={styles.section}>
      <Reveal className={styles.card}>
        <Image
          src={ctaImg}
          alt={t("scholarship.v2.cta.imagealt")}
          className={styles.image}
          width="100%"
          height="100%"
        />

        <span className={styles.tint} aria-hidden="true" />
        <span className={styles.scrim} aria-hidden="true" />
        <span className={styles.scrimBottom} aria-hidden="true" />

        <div className={styles.content}>
          <div className={styles.block}>
            <div className={styles.text}>
              <h2 className={styles.heading}>
                {t("scholarship.v2.cta.heading.line1")}
                <br />
                {t("scholarship.v2.cta.heading.line2")}
              </h2>
              <p className={styles.sub}>{t("scholarship.v2.cta.sub")}</p>
            </div>

            <CustomLink
              href="/scholarship"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                openForm();
              }}
              className={styles.button}
              dataTestId="scholarship-v2-cta-apply"
            >
              {t("scholarship.v2.applynow")}
            </CustomLink>
          </div>
        </div>
      </Reveal>
    </section>
  );
};

export default wrapperHOC(Cta, {
  componentName: "Cta-ScholarshipV2",
  showForChina: true,
});
